import { createUseStyles } from 'react-jss';
import { colors } from '@insurancemenu/imenu-components-lib';
import { ComponentStylesProps } from '@insurancemenu/imenu-components-lib/dist/types';

export interface InstalledPortfolioCardThemeProps {
  isTerminated: boolean;
  productsLength: number;
  ratesBlockHeight: string;
  hasMultiProductsPortfolio: boolean;
  showProductLine: boolean;
}

const styles: ComponentStylesProps<InstalledPortfolioCardThemeProps> = {
  productCard: ({ isTerminated, productsLength, hasMultiProductsPortfolio }) => ({
    border: `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
    backgroundColor: isTerminated ? '#FFF9F9' : '#F9FBFF',
    borderRadius: '1.5rem',
    padding: productsLength === 1 ? '2rem' : '1rem 1rem 2rem',
    boxSizing: 'border-box',
    // eslint-disable-next-line no-nested-ternary
    width: productsLength === 1 ? '26.5rem' : productsLength <= 3 ? `${26.5 * productsLength + 3}rem` : '100%',
    display: 'grid',
    alignItems: hasMultiProductsPortfolio ? 'self-start' : 'stretch',
    position: 'relative',
  }),
  multiProductCard: {
    display: 'flex',
    flexDirection: 'column',
  },
  // for installed stage
  singleProductWrapper: ({ showProductLine }) => ({
    display: 'grid',
    // Product line isn't shown when installed card is in Renewal Stage
    // Last 3rem row is needed to keep view as in renewal portfolio card
    gridTemplateRows: showProductLine ? `auto auto 3.5rem 1fr` : `auto auto 3.5rem 1fr 3rem`,
    gap: '1.5rem',
  }),
  productsList: () => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 25rem)',
    gap: '2rem',
    justifyContent: 'center',
    // Uncomment this when installed card will have button on the bottom
    // borderBottom: `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
    // paddingBottom: '2rem',
    // marginBottom: '2rem',
  }),
  multiProductWrapper: ({ isTerminated }) => ({
    display: 'grid',
    gap: '1.5rem',
    backgroundColor: colors.neutral.white,
    width: '25rem',
    borderRadius: '1.5rem',
    padding: '2rem',
    boxSizing: 'border-box',
    border: `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
    gridAutoRows: 'auto 1fr',
  }),
  productHeader: ({ productsLength, showProductLine }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: productsLength === 1 ? 'flex-start' : 'center',
    marginLeft: productsLength === 1 ? '0rem' : '0.5rem',
    marginTop: showProductLine ? '3.6rem' : '1rem',
  }),
  productLineTitle: ({ productsLength }) => ({
    marginBottom: productsLength === 1 && '1rem',
    marginTop: productsLength === 1 && 0,
  }),
  dateStatus: ({ productsLength }) => ({
    fontSize: '1.4rem',
    lineHeight: '2rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    textAlign: 'right',
    display: 'flex',
    flexDirection: productsLength === 1 ? 'column' : 'row',
    '& span:not(:last-child)': {
      marginRight: productsLength > 1 && '0.5rem',
    },
    margin: {
      top: productsLength === 1 ? '0.2rem' : 0,
      bottom: 0,
      right: productsLength > 1 && '1rem',
    },
  }),
  productTitle: {
    fontSize: '1.6rem',
    lineHeight: '2.4rem',
    color: colors.neutral[900],
    margin: 0,
    letterSpacing: '0.02em',
    fontWeight: '400',
  },
  singleProductPortfolioTitle: {
    alignSelf: 'self-start',
    marginBottom: 0,
  },
  multiProductPortfolioTitle: {
    paddingLeft: `1rem`,
    marginBottom: 0,
    maxWidth: '60%',
  },
  multiProductHeader: ({ isTerminated, showProductLine }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: showProductLine ? '0 0 2rem' : '2.5rem 0 2rem',
    marginBottom: '2rem',
    borderBottom: `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
  }),
  ratesItem: ({ isTerminated }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'center',
    fontSize: '1.4rem',
    lineHeight: '2rem',
    letterSpacing: '0.02em',
    '& span:first-child': {
      overflowWrap: 'break-word',
      maxWidth: '12rem',
    },
    '& span:last-child': {
      fontWeight: 700,
      color: isTerminated ? colors.red[600] : colors.blue[600],
    },
    '&:not(:last-child)': {
      marginBottom: '0.8rem',
    },
  }),
  downloadButton: ({ productsLength }) => ({
    textTransform: 'uppercase',
    padding: 0,
    marginRight: productsLength > 1 && '1rem',
  }),
  ratesWrapper: ({ productsLength, ratesBlockHeight }) => ({
    minHeight: productsLength === 1 ? ratesBlockHeight : 'auto',
    // Uncomment this when installed card will have button on the bottom
    // borderBottom: productsLength === 1 && `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
  }),
  ratesTitle: ({ isTerminated }) => ({
    font: { size: '1.4rem', lineHeight: '2rem' },
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: colors.neutral[700],
    textTransform: 'uppercase',
    margin: 0,
    paddingTop: '1.5rem',
    borderTop: `0.1rem solid ${isTerminated ? colors.red[200] : colors.blue[300]}`,
  }),
  ratesList: {
    maxWidth: '24.3rem',
    padding: '0',
    margin: 0,
  },
  ratesMessage: {
    fontSize: '1.4rem',
    color: colors.neutral[900],
    display: 'flex',
    alignItems: 'center',
    '& span': {
      marginLeft: '1rem',
    },
  },
};

const createStyles = createUseStyles(styles);
export { createStyles };
